/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import AdminGroups from '@ion/applications/src/pages/admin/accounts/AdminGroups';
import {EditGroupEditor} from '@ion/applications/src/pages/admin/accounts/EditGroupEditor';
import {auditAdminGroup, showAuditChangesDialog} from '@ion/applications/src/pages/admin/accounts/state';
import {
  adminGroupsAuditIsEditingSelector,
  adminGroupsAuditRecordSelector,
  adminGroupsAuditSelectedSelector,
  adminGroupsAuditShowChangesDialogSelector,
  adminGroupsEditIsEditingSelector,
  adminGroupsEditIsViewModeSelector
} from '@ion/applications/src/pages/admin/accounts/state-selectors';
import AdminAssets from '@ion/applications/src/pages/admin/assets/AdminAssets';
import Groups from '@ion/applications/src/pages/admin/groups/Groups';
import AdminUsers from '@ion/applications/src/pages/admin/users/AdminUsers';
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import Devices from '@ion/applications/src/pages/devices/Devices';
import {translate} from '@ion/core/src/locale/locale';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  AccountLink,
  CRUDPermissions,
  Entity,
  GenericObject,
  GlobalSearchModule
} from '@ion/core/src/types/entities';
import {
  Operation
} from '@ion/core/src/types/general';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {
  AuditDateFilterProps,
  AuditDialog
} from '@ion/dls/src/editors/audit/AuditDialog';
import {
  ViewAuditChangesDialog
} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useCloseKeyCommand} from '@ion/dls/src/hooks/useCloseKeyCommand';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import GlobalSearchTitle from './GlobalSearchTitle';
import GlobalSearchWidget from './GlobalSearchWidget';
import {globalGroupSelector, globalSearchSelector} from './selector';

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '1067px',
    minWidth                       : '1067px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)',
    minHeight : 'calc(75% - 94px)'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  closeButton : {
    top   : '26px',
    width : '3%'
  },
  titleContainer : {
    width : '70%'
  },
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`,
    paddingTop   : '20px'
  },
  formTitle : {
    justifyContent : 'left',
    alignItems     : 'center',
    marginLeft     : '10px'
  },
  formDialogContent : {
    overflow   : 'auto',
    paddingTop : '0px'
  }
});

// defines the global search editor prop definitions ...
export interface GlobalSearchEditorProps extends AuditDateFilterProps,
  DropDownInfiniteScrollProps,
  AssetTreeRefresh {
  getRouteLabel: (route: string, useTitleCase: boolean) => string;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onSelectAccount: (value: AccountLink) => void;
  selectedJobId: (jobId: number) => void;
  refreshInitialization?: Function;
  defaultPageId: string;
  Features?: IApplicationFeatures;
  isCtcAdmin?: boolean;
  includeGroups?: boolean;
  omitHeader?: boolean;
  showTimeoutError?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  groupsPermissions?: CRUDPermissions;
  usersPermissions?: CRUDPermissions;
  assetsPermissions?: CRUDPermissions;
  accountsPermissions?: CRUDPermissions;
  devicesPermissions?: CRUDPermissions;
  customVisionPermissions?: CRUDPermissions;
  hasCreateJob?: boolean;
  isGlobalSearchMaxLength?: boolean;
  usePreferedPageSize?: boolean;
  hasInheritableGroups?: boolean;
  hasSsoConfigPermission?: boolean;
  accountsMap?: GenericObject;
  hasCarbFeature?: boolean;
}

/**
 * The Global Search Editor will display data based on search keyword and selected module
 */
export const GlobalSearchEditor = ({
  Features,
  getRouteLabel,
  defaultPageId,
  onSelectAccount,
  onCloseDialog,
  selectedJobId,
  omitHeader,
  refreshInitialization,
  groupsPermissions,
  usersPermissions,
  assetsPermissions,
  accountsPermissions,
  devicesPermissions,
  customVisionPermissions,
  hasCreateJob,
  accountsMap,
  hasSsoConfigPermission = false,
  isCtcAdmin = false,
  includeGroups = false,
  showTimeoutError = false,
  includeGroupProperties = true,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  hasInheritableGroups = false,
  enableDateFilter = false,
  isInfiniteScroll = false,
  isGlobalSearchMaxLength = false,
  usePreferedPageSize = false,
  hasCarbFeature = false,
  showTreeRefresh = false
}: GlobalSearchEditorProps) => {
  const editorStyles = useExtendStyles(styles, styleOverrides);
  const classes      = editorStyles();
  const dispatch     = useDispatch();
  const {account}    = useAuthenticatedUser();
  const onEscape     = useCloseKeyCommand(onCloseDialog);

  const showEditGroup                = useSelector(adminGroupsEditIsEditingSelector);
  const showViewGroup                = useSelector(adminGroupsEditIsViewModeSelector);
  const showAuditGroup               = useSelector(adminGroupsAuditIsEditingSelector);
  const auditGroup                   = useSelector(adminGroupsAuditSelectedSelector);
  const selectedAuditRecord          = useSelector(adminGroupsAuditRecordSelector);
  const shouldShowAuditChangesDialog = useSelector(adminGroupsAuditShowChangesDialogSelector);

  const {
    hasUpdatePermission: hasCustomVisionUpdatePermission
  } = customVisionPermissions || {};

  const {
    accountHierarchy,
    globalGroup
  } = useSelector(globalGroupSelector);

  const {
    searchKeyword,
    searchModule,
    search : {
      isViewing: open
    }
  } = useSelector(globalSearchSelector);

  const onAuditClose = useCallback(() => {
    dispatch(auditAdminGroup(null, false));
  }, []);

  const onViewAuditChanges = useCallback(function(auditRecord, show = false) {
    dispatch(showAuditChangesDialog(auditRecord, show));
  }, []);

  const onViewAuditChangesClose = useCallback(() => {
    dispatch(showAuditChangesDialog(null, false));
  }, []);

  const applicationPermissions = {
    hasAddPermission : Features.hasPermission(Entity.application, Operation.Create)
  };

  return (
    <Dialog open={open}
      onKeyDown={onEscape}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCloseDialog} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <div className={classes.titleContainer}>
            <GlobalSearchTitle searchKeyword={searchKeyword}
              searchModule={searchModule} />
          </div>
          <GlobalSearchWidget isGlobalSearchMaxLength={isGlobalSearchMaxLength}/>
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        <div className={classes.adminGrid}>
          {searchModule === GlobalSearchModule.Accounts && (
            <AdminGroups Features={Features}
              isCtcAdmin
              includeContacts
              includeAddresses
              includeApplications
              includeAccountContacts
              hasCarbFeature={hasCarbFeature}
              allowSSOConfig={hasSsoConfigPermission}
              includeGroupProperties={false}
              usePreferedPageSize={usePreferedPageSize}
              enableDateFilter={enableDateFilter}
              includeGlobalSearch={open}
              searchKeyword={searchKeyword}
              defaultAccount={account}
              onSelectAccount={onSelectAccount}
              accountsPermissions={accountsPermissions}
              applicationPermissions={applicationPermissions}
              refreshInitialization={refreshInitialization}
              accountHierarchy={accountHierarchy} />
          )}
          {searchModule === GlobalSearchModule.Users && (
            <AdminUsers Features={Features}
              isCtcAdmin={isCtcAdmin}
              usePreferedPageSize={usePreferedPageSize}
              context={UserContext.ctcAdmin}
              includeApplications
              includeAddresses
              includeContacts
              enableDateFilter={enableDateFilter}
              defaultPageId={defaultPageId}
              getRouteLabel={getRouteLabel}
              includeGlobalSearch={open}
              searchKeyword={searchKeyword}
              usersPermissions={usersPermissions}
              onSelectAccount={onSelectAccount}
              refreshInitialization={refreshInitialization}
              accountHierarchy={accountHierarchy} />
          )}
          {searchModule === GlobalSearchModule.Groups && (
            <Groups Features={Features}
              isCtcAdmin={isCtcAdmin}
              usePreferedPageSize={usePreferedPageSize}
              enableDateFilter={enableDateFilter}
              includeGlobalSearch={open}
              searchKeyword={searchKeyword}
              onSelectAccount={onSelectAccount}
              groupsPermissions={groupsPermissions}
              accountsMap={accountsMap}
              hasInheritableGroups={hasInheritableGroups}
              accountHierarchy={accountHierarchy}
              refreshInitialization={refreshInitialization}
              includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
              includeAssetGridCheckboxes={includeAssetGridCheckboxes} />
          )}
          {(searchModule === GlobalSearchModule.Assets || searchModule === GlobalSearchModule.AssetsVin) && (
            <AdminAssets Features={Features}
              isCtcAdmin={isCtcAdmin}
              usePreferedPageSize={usePreferedPageSize}
              showTimeoutError={showTimeoutError}
              enableDateFilter={enableDateFilter}
              context={UserContext.ctcAdmin}
              defaultPageId={defaultPageId}
              getRouteLabel={getRouteLabel}
              globalGroup={globalGroup}
              includeGlobalSearch={open}
              searchKeyword={searchKeyword}
              searchModule={searchModule}
              onSelectAccount={onSelectAccount}
              accountHierarchy={accountHierarchy}
              assetsPermissions={assetsPermissions}
              refreshInitialization={refreshInitialization}
              includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
              includeAssetGridCheckboxes={includeAssetGridCheckboxes}
              isInfiniteScroll={isInfiniteScroll}
              showTreeRefresh={showTreeRefresh} />
          )}
          {searchModule === GlobalSearchModule.Devices && (
            <Devices onSelectAccount={onSelectAccount}
              omitHeader={omitHeader}
              selectedJobId={selectedJobId}
              accountHierarchy={accountHierarchy}
              enableDateFilter={enableDateFilter}
              globalGroup={globalGroup}
              Features={Features}
              searchKeyword={searchKeyword}
              includeGroups={includeGroups}
              isCtcAdmin={isCtcAdmin}
              usePreferedPageSize={usePreferedPageSize}
              includeGlobalSearch={open}
              hasCreateJob={hasCreateJob}
              devicesPermissions={devicesPermissions}
              includeGroupProperties={includeGroupProperties} />
          )}
          {(showEditGroup || showViewGroup) && (<EditGroupEditor isCtcAdmin
            hasCarbFeature={hasCarbFeature}
            allowSSOConfig={hasSsoConfigPermission}
            includeApplications
            isViewDialog={showViewGroup}
            defaultAccount={account}
            accountsPermissions={accountsPermissions}
            applicationPermissions={applicationPermissions}
            hasCustomVisionPermission={hasCustomVisionUpdatePermission}
            refreshInitialization={refreshInitialization} />
          )}
          {showAuditGroup &&
            <AuditDialog enableDateFilter={enableDateFilter}
              item={auditGroup}
              entity={Entity.account}
              onCloseDialog={onAuditClose}
              rowMenusEnabled={isCtcAdmin}
              showDifferences={isCtcAdmin}
              onViewAuditChanges={onViewAuditChanges} />}
          {shouldShowAuditChangesDialog && selectedAuditRecord && (
            <ViewAuditChangesDialog auditRecord={selectedAuditRecord}
              onCloseDialog={onViewAuditChangesClose}
              entity={Entity.account} />
          )}
        </div>
      </DialogContent>
      <DialogActions className={classes.formActions}>
        <TertiaryButton onClick={onCloseDialog}>
          {translate('common:editors.shared.close')}
        </TertiaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalSearchEditor;
